import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '../lib/supabase';
import { selectPendingPromptData } from '../store/selectors';
import './Auth.css';

const AuthComponent = () => {
  const navigate = useNavigate();
  const pendingPromptData = useSelector(selectPendingPromptData);
  const [isGoogleLoading, setIsGoogleLoading] = React.useState(false);

  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const isRecoveryFromHash = hashParams.get('type') === 'recovery';
  const [isRecoveryMode, setIsRecoveryMode] = React.useState(isRecoveryFromHash);
  const [showUI, setShowUI] = React.useState(!isRecoveryFromHash);

  // Helper function to bind Amplitude user
  const bindAmplitudeUser = (user) => {
    if (user && window.amplitude) {
      try {
        window.amplitude.setUserId(user.id);
        // Use identify API for user properties (new Amplitude SDK)
        const identifyObj = new window.amplitude.Identify();
        identifyObj.set('email', user.email);
        window.amplitude.identify(identifyObj);
        console.log('[Auth] Amplitude user bound:', user.id);
      } catch (e) {
        console.warn('[Auth] Amplitude binding failed:', e);
      }
    }
  };

  useEffect(() => {
    if (isRecoveryFromHash) {
      setIsRecoveryMode(true);
      const timer = setTimeout(() => {
        setShowUI(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isRecoveryFromHash]);

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const isRecovery = hashParams.get('type') === 'recovery';

    const checkAuthAndRedirect = async () => {
      if (isRecovery) {
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        // Bind Amplitude user for already logged in users
        bindAmplitudeUser(session.user);

        if (pendingPromptData) {
          navigate('/result');
        } else {
          navigate('/');
        }
      }
    };

    checkAuthAndRedirect();

    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'SUPABASE_AUTH_SUCCESS') {
        const session = event.data.session;

        if (session) {
          const { error } = await supabase.auth.setSession({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
          });

          if (error) {
            console.error('[Auth] Error setting session:', error);
            return;
          }

          console.log('[Auth] Session set successfully');

          // Bind Amplitude user after popup login
          bindAmplitudeUser(session.user);

          if (pendingPromptData) {
            navigate('/result');
          } else {
            navigate('/');
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        return;
      }

      if (event === 'USER_UPDATED') {
        return;
      }

      if (event === 'SIGNED_IN' && session?.user) {
        // Bind Amplitude user on sign in
        bindAmplitudeUser(session.user);

        if (pendingPromptData) {
          navigate('/result');
        } else {
          navigate('/');
        }
      }
    });

    return () => {
      window.removeEventListener('message', handleMessage);
      subscription.unsubscribe();
    };
  }, [navigate, pendingPromptData]);

  if (!showUI) {
    return (
      <div className="auth-container">
        <div className="auth-background">
          <img src="/auth-background.png" alt="Background" />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-background">
        <img src="/auth-background.png" alt="Background" />
      </div>
      <div className="auth-content">
        <h1 className="auth-title">
          {isRecoveryMode ? 'Reset Your Password' : 'Sign In to see your latest results!'}
        </h1>
        <div className="auth-form">
          {!isRecoveryMode && (
            <button
              className="google-sign-in-button"
              disabled={isGoogleLoading}
              onClick={async () => {
                setIsGoogleLoading(true);
                const { data, error } = await supabase.auth.signInWithOAuth({
                  provider: 'google',
                  options: {
                    skipBrowserRedirect: true,
                    redirectTo: `${window.location.origin}/auth/callback`,
                  }
                });

                if (error) {
                  console.error('[Auth] Error signing in with Google:', error);
                  setIsGoogleLoading(false);
                  return;
                }

                if (data?.url) {
                  const width = 500;
                  const height = 600;
                  const left = window.screen.width / 2 - width / 2;
                  const top = window.screen.height / 2 - height / 2;

                  const popup = window.open(
                    data.url,
                    'Google Sign In',
                    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
                  );

                  if (!popup || popup.closed || typeof popup.closed === 'undefined') {
                    alert('Please allow popups for this site to sign in with Google');
                    setIsGoogleLoading(false);
                    return;
                  }

                  const checkPopup = setInterval(() => {
                    if (popup.closed) {
                      clearInterval(checkPopup);
                      setTimeout(() => {
                        setIsGoogleLoading(false);
                      }, 2000);
                    }
                  }, 500);
                } else {
                  setIsGoogleLoading(false);
                }
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" className="google-icon">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span>Continue with Google</span>
              {isGoogleLoading && (
                <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                </svg>
              )}
            </button>
          )}
          {!isRecoveryMode && (
            <div className="auth-divider">
              <span>or</span>
            </div>
          )}
          <Auth
            supabaseClient={supabase}
            view={isRecoveryMode ? "update_password" : "sign_in"}
            appearance={{
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    brand: '#82CD59',
                    brandAccent: '#82CD59',
                  }
                }
              },
              style: {
                button: {
                  backgroundColor: '#82CD59',
                  color: '#000000',
                },
                input: {
                  color: '#ffffff',
                  caretColor: '#ffffff',
                }
              }
            }}
            providers={[]}
            redirectTo={`${window.location.origin}/auth/callback`}
          />
        </div>
      </div>
    </div>
  );
};

export default AuthComponent;